// localStorage persistence for schedules, special SK entries and assignment history.

// keys are namespaced per area (Palia Kalan keeps the original keys)
const AREA = localStorage.getItem("rssb_active_area") || "palia";
const ns = (k) => (AREA === "palia" ? k : `${k}__${AREA}`);
const KEYS = {
  schedules: ns("rssb_schedules"),
  special: ns("rssb_special_sk"),
  history: ns("rssb_history"),
};

const read = (k, fallback) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

export const monthKey = (year, month) => `${year}-${month}`;

export const getSchedules = () => read(KEYS.schedules, {});
const SCHEDULE_VERSION = 12; // bump to invalidate stale saved schedules after data/logic changes
export const getSchedule = (year, month) => {
  const s = getSchedules()[monthKey(year, month)] || null;
  if (s && s._v !== SCHEDULE_VERSION) return null;
  return s;
};
export const saveSchedule = (year, month, data) => {
  const all = getSchedules();
  all[monthKey(year, month)] = { ...data, _v: SCHEDULE_VERSION };
  write(KEYS.schedules, all);
};
// Valid (current-version) schedules keyed by "YYYY-M"
export const getSavedSchedules = () =>
  Object.fromEntries(Object.entries(getSchedules()).filter(([, s]) => s?._v === SCHEDULE_VERSION && s.result?.variants?.length));
export const deleteSchedule = (year, month) => {
  const all = getSchedules();
  delete all[monthKey(year, month)];
  write(KEYS.schedules, all);
};

// Sewadars on leave per month: { "YYYY-M": [ids] }
const LEAVE_KEY = ns("rssb_leave");
export const getLeave = (year, month) => read(LEAVE_KEY, {})[monthKey(year, month)] || [];
export const saveLeave = (year, month, ids) => {
  const all = read(LEAVE_KEY, {});
  all[monthKey(year, month)] = ids;
  write(LEAVE_KEY, all);
};

// Named saved copies of schedules (system / manual / PDF-imported)
const SNAP_KEY = ns("rssb_saved_schedules");
export const getSnapshots = () => read(SNAP_KEY, []);
export const saveSnapshot = (snap) => write(SNAP_KEY, [snap, ...getSnapshots().filter((s) => s.id !== snap.id)]);
export const deleteSnapshot = (id) => write(SNAP_KEY, getSnapshots().filter((s) => s.id !== id));

export const getSpecialAll = () => read(KEYS.special, {});
export const getSpecial = (year, month) => getSpecialAll()[monthKey(year, month)] || [];
export const saveSpecial = (year, month, entries) => {
  const all = getSpecialAll();
  all[monthKey(year, month)] = entries;
  write(KEYS.special, all);
};

export const getHistory = () => read(KEYS.history, []);
// Commit a selected schedule's assignments into rolling history (keep last 3 months).
export const commitHistory = (year, month, variant) => {
  const mk = monthKey(year, month);
  let hist = getHistory().filter((h) => h.monthKey !== mk);
  const entries = [];
  Object.entries(variant.cells || {}).forEach(([gharId, dates]) => {
    Object.values(dates).forEach((cell) => {
      if (cell.preacherId || cell.pathiId) {
        entries.push({ monthKey: mk, gharId, preacherId: cell.preacherId, pathiId: cell.pathiId });
      }
    });
  });
  hist = [...hist, ...entries];
  // keep only last 3 distinct month keys
  const months = [...new Set(hist.map((h) => h.monthKey))].sort().slice(-3);
  hist = hist.filter((h) => months.includes(h.monthKey));
  write(KEYS.history, hist);
};
